import { Radar, Practice } from '../../domain/entities/RadarTypes';
import { RadarRepositoryPort } from '../../application/ports/RadarRepositoryPort';
import { PersistenceAdapterPort } from './adapters/PersistenceAdapterPort';
import { QUADRANT_NAMES } from '../../domain/entities/RadarTypes';

const STORAGE_KEY = 'quality-radar-data';
const DEV_DATA_URL = '/radar-data.json';

export class LocalStorageRadarRepository implements RadarRepositoryPort {
  private initialized = false;

  constructor(private persistenceAdapter?: PersistenceAdapterPort) {
    // Immediately initialize dev data when repository is created
    this.initializeDevData().catch(err => {
      console.error(
        '[LocalStorageRadarRepository] Failed to initialize dev data during construction:',
        err
      );
    });
  }

  private async initializeDevData(): Promise<void> {
    try {
      const existingData = await this.getStorage();

      if (existingData) {
        this.initialized = true;
        return;
      }

      // Only try to fetch from URL in browser environment
      if (typeof window !== 'undefined') {
        try {
          // Use full URL with origin to avoid URL parsing issues
          const fullUrl = new URL(DEV_DATA_URL, window.location.origin).href;

          console.log(
            '[LocalStorageRadarRepository] Fetching dev data from:',
            fullUrl
          );

          // First attempt with regular fetch
          let response;
          try {
            response = await fetch(fullUrl);
          } catch (fetchError) {
            console.warn(
              '[LocalStorageRadarRepository] Primary fetch failed:',
              fetchError
            );

            // Try alternative approach with different URL format
            const alternativeUrl = `${window.location.origin}${DEV_DATA_URL}`;
            console.log(
              '[LocalStorageRadarRepository] Trying alternative URL:',
              alternativeUrl
            );
            response = await fetch(alternativeUrl);
          }

          if (!response.ok) {
            throw new Error(
              `Failed to fetch radar data: ${response.statusText}`
            );
          }

          const data = await response.json();

          if (!data.radars || !Array.isArray(data.radars)) {
            console.error('[DevData] Invalid radar data format:', data);
            return;
          }

          await this.setStorage(JSON.stringify(data.radars));
          this.initialized = true;
        } catch (error) {
          console.error(
            '[DevData] Failed to fetch or process radar data:',
            error
          );
          return;
        }
      } else {
        // Basic mock data for tests
        const mockRadars = [
          {
            id: 'test-radar',
            name: 'Test Radar',
            tenantId: 'test-tenant',
            teamId: 'test-team',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            practices: [],
            quadrants: QUADRANT_NAMES,
          },
        ];

        await this.setStorage(JSON.stringify(mockRadars));
        this.initialized = true;
      }
    } catch (error) {
      console.error('[DevData] Error initializing dev data:', error);
    }
  }

  private async getStorage(): Promise<string | null> {
    if (this.persistenceAdapter) {
      return this.persistenceAdapter.get(STORAGE_KEY);
    }

    try {
      // Access localStorage safely
      if (typeof localStorage !== 'undefined') {
        return localStorage.getItem(STORAGE_KEY);
      }
    } catch (error) {
      console.error(
        '[LocalStorageRadarRepository] Error accessing localStorage:',
        error
      );
    }

    return null;
  }

  private async setStorage(value: string): Promise<void> {
    if (this.persistenceAdapter) {
      await this.persistenceAdapter.set(STORAGE_KEY, value);
      return;
    }

    try {
      // Access localStorage safely
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, value);
      }
    } catch (error) {
      console.error(
        '[LocalStorageRadarRepository] Error writing to localStorage:',
        error
      );
    }
  }

  async createRadar(
    tenantId: string,
    teamId: string,
    radar: Omit<Radar, 'id' | 'tenantId' | 'teamId' | 'createdAt' | 'updatedAt'>
  ): Promise<Radar> {
    await this.initializeDevData();
    const newRadar: Radar = {
      id: crypto.randomUUID(),
      tenantId,
      teamId,
      ...radar,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    const radars = await this.findAllByTeam(tenantId, teamId);
    await this.setStorage(JSON.stringify([...radars, newRadar]));
    return newRadar;
  }

  async save(radar: Radar): Promise<void> {
    await this.initializeDevData();
    const radars = await this.findAll();
    const existingIndex = radars.findIndex(r => r.id === radar.id);
    if (existingIndex >= 0) {
      radars[existingIndex] = radar;
    } else {
      radars.push(radar);
    }
    await this.setStorage(JSON.stringify(radars));
  }

  async findById(
    tenantId: string,
    teamId: string,
    id: string
  ): Promise<Radar | null> {
    await this.initializeDevData();
    const radars = await this.findAllByTeam(tenantId, teamId);
    const radar = radars.find(r => r.id === id);
    if (!radar) return null;
    return this.parseRadarDates(radar);
  }

  async findAll(): Promise<Radar[]> {
    // Ensure dev data is initialized
    await this.initializeDevData();

    // Get data from storage
    const data = await this.getStorage();
    if (!data) {
      return [];
    }

    try {
      // Parse the data
      const radars = JSON.parse(data) as Radar[];

      if (radars.length === 0 && typeof window !== 'undefined') {
        // Force refresh of data
        this.initialized = false;
        await this.initializeDevData();

        // Try again
        const refreshedData = await this.getStorage();
        if (refreshedData) {
          const refreshedRadars = JSON.parse(refreshedData) as Radar[];
          return refreshedRadars.map(radar => this.parseRadarDates(radar));
        }
      }

      // Parse dates and return
      return radars.map(radar => this.parseRadarDates(radar));
    } catch (error) {
      // If parsing fails, try to reinitialize in browser environment
      if (typeof window !== 'undefined') {
        // Force refresh of data
        localStorage.removeItem(STORAGE_KEY);
        this.initialized = false;
        await this.initializeDevData();

        // Try again with fresh data
        const refreshedData = await this.getStorage();
        if (refreshedData) {
          try {
            const refreshedRadars = JSON.parse(refreshedData) as Radar[];
            return refreshedRadars.map(radar => this.parseRadarDates(radar));
          } catch (secondError) {
            console.error(
              '[LocalStorageRadarRepository] Failed to recover after parsing error:',
              secondError
            );
          }
        }
      }
      return [];
    }
  }

  async findAllByTeam(tenantId: string, teamId: string): Promise<Radar[]> {
    // Make sure dev data is loaded before proceeding
    await this.initializeDevData();

    // Get all radars
    const radars = await this.findAll();

    // If there are no radars and we're still in the browser, try to reinitialize
    if (radars.length === 0 && typeof window !== 'undefined') {
      // Force reinitialization
      this.initialized = false;
      await this.initializeDevData();

      // Try to get all radars again
      const retriedRadars = await this.findAll();

      if (retriedRadars.length > 0) {
        // Process and return filtered radars
        const filteredRadars = retriedRadars
          .filter(r => r.tenantId === tenantId && r.teamId === teamId)
          .map(radar => this.parseRadarDates(radar));

        return filteredRadars;
      }
    }

    // Process and return filtered radars from original attempt
    const filteredRadars = radars
      .filter(r => r.tenantId === tenantId && r.teamId === teamId)
      .map(radar => this.parseRadarDates(radar));

    // If still no radars found, check if we have the expected radar data
    if (filteredRadars.length === 0 && typeof window !== 'undefined') {
      try {
        // Try loading the JSON file directly - with proper URL in browser environment
        const fullUrl = new URL(DEV_DATA_URL, window.location.origin).href;

        const response = await fetch(fullUrl);
        if (response.ok) {
          const data = await response.json();
          if (
            data.radars &&
            Array.isArray(data.radars) &&
            data.radars.length > 0
          ) {
            // Find matching radars in the direct data
            const matchingRadars = data.radars
              .filter(
                (
                  r: Omit<Radar, 'createdAt' | 'updatedAt'> & {
                    createdAt: string;
                    updatedAt: string;
                  }
                ) => r.tenantId === tenantId && r.teamId === teamId
              )
              .map(
                (
                  r: Omit<Radar, 'createdAt' | 'updatedAt'> & {
                    createdAt: string;
                    updatedAt: string;
                  }
                ) => this.parseRadarDates(r as unknown as Radar)
              );

            if (matchingRadars.length > 0) {
              return matchingRadars;
            }
          }
        }
      } catch (error) {
        console.error(
          '[LocalStorageRadarRepository] Error loading fallback data:',
          error
        );
      }
    }

    return filteredRadars;
  }

  async updateRadar(
    tenantId: string,
    teamId: string,
    id: string,
    radar: Partial<Radar>
  ): Promise<Radar> {
    const allRadars = await this.findAll();
    const updatedRadars = allRadars.map(r =>
      r.id === id && r.tenantId === tenantId && r.teamId === teamId
        ? { ...r, ...radar, updatedAt: new Date() }
        : r
    );
    await this.setStorage(JSON.stringify(updatedRadars));
    const updated = updatedRadars.find(
      r => r.id === id && r.tenantId === tenantId && r.teamId === teamId
    );
    if (!updated) throw new Error('Radar not found after update');
    return updated;
  }

  async deleteRadar(
    tenantId: string,
    teamId: string,
    id: string
  ): Promise<void> {
    const allRadars = await this.findAll();
    const filteredRadars = allRadars.filter(
      r => !(r.id === id && r.tenantId === tenantId && r.teamId === teamId)
    );
    await this.setStorage(JSON.stringify(filteredRadars));
  }

  async addPractice(
    tenantId: string,
    teamId: string,
    radarId: string,
    practice: Omit<Practice, 'id'>
  ): Promise<Radar> {
    const newPractice = { ...practice, id: crypto.randomUUID() };
    const radar = await this.findById(tenantId, teamId, radarId);
    if (!radar) throw new Error('Radar not found');
    const updatedRadar = {
      ...radar,
      practices: [...radar.practices, newPractice],
      updatedAt: new Date(),
    };
    await this.save(updatedRadar);
    return updatedRadar;
  }

  async updatePractice(
    tenantId: string,
    teamId: string,
    radarId: string,
    practiceId: string,
    practice: Partial<Practice>
  ): Promise<Radar> {
    const radar = await this.findById(tenantId, teamId, radarId);
    if (!radar) throw new Error('Radar not found');
    const practices = radar.practices.map(p =>
      p.id === practiceId ? { ...p, ...practice } : p
    );
    await this.updateRadar(tenantId, teamId, radarId, { practices });
    const updated = await this.findById(tenantId, teamId, radarId);
    if (!updated) throw new Error('Radar not found after practice update');
    return updated;
  }

  async deletePractice(
    tenantId: string,
    teamId: string,
    radarId: string,
    practiceId: string
  ): Promise<void> {
    const radar = await this.findById(tenantId, teamId, radarId);
    if (!radar) throw new Error('Radar not found');
    await this.updateRadar(tenantId, teamId, radarId, {
      practices: radar.practices.filter(p => p.id !== practiceId),
    });
  }

  private parseRadarDates(radar: Radar): Radar {
    try {
      return {
        ...radar,
        createdAt:
          radar.createdAt instanceof Date
            ? radar.createdAt
            : new Date(radar.createdAt),
        updatedAt:
          radar.updatedAt instanceof Date
            ? radar.updatedAt
            : new Date(radar.updatedAt),
      };
    } catch (error) {
      // Return the original radar if date parsing fails
      return radar;
    }
  }
}
